import { WhiteBox, FoodsBadge } from "@foodpilot/foods";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ValidatedState, ValidatedTag } from "../../ValidatedTag";
import { QuestionnaireOverview } from "src/api/client-api/company/overview/useOrganizationOverviewOneSsq";
import { DIAGNOSTIC_COLOR_INDEX } from "../Diagnostic";
import { useTranslation } from "react-i18next";
import { GoToSsqButton } from "src/components/Button/GoToSsqButton";
import { URLHandler } from "src/services/URLHandler";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { CircularProgress } from "src/components/CircularProgress";

type Props = {
    overview: QuestionnaireOverview;
};
export const Questionnaire = (props: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { overview } = props;
    const datavizColor = theme.custom.dataviz.colorsWithExtra[DIAGNOSTIC_COLOR_INDEX];

    const completion = overview?.post?.completion ?? 0;
    const completionLabel = `${completion}%`;
    const completionInfo = t("company.dashboard.percent_subtitle");

    return (
        <WhiteBox>
            <Stack padding={{ xs: theme.spacing(2), sm: theme.spacing(3) }} gap={theme.spacing(3)}>
                <QuestionnaireHeader overview={overview} />
                <Box
                    sx={{
                        width: "100%",
                        backgroundColor: datavizColor.variant[100],
                        borderRadius: "8px",
                    }}
                >
                    <Stack
                        flexDirection="row"
                        padding={theme.spacing(1)}
                        gap={theme.spacing(1.5)}
                        alignItems={"center"}
                    >
                        <CircularProgress progress={completion} color={datavizColor.main} />
                        <Stack sx={{ ml: theme.spacing(8) }}>
                            <Typography variant="h3">{completionLabel}</Typography>
                            <Typography variant="body">{completionInfo}</Typography>
                        </Stack>
                    </Stack>
                </Box>
            </Stack>
        </WhiteBox>
    );
};

const QuestionnaireHeader = (props: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { company } = useCompanyMetadata();
    const { currentCampaign } = useFoodpilotContext();
    const { overview } = props;

    const scoresOverview = overview.scores ?? [];
    const firstScoreId = scoresOverview.find((i) => true)?.id;
    const validatedState: ValidatedState = overview?.post?.isLocked === true ? "validated" : null;
    const datavizColors = theme.custom.dataviz.colorsWithExtra[DIAGNOSTIC_COLOR_INDEX];

    const url =
        overview.id && currentCampaign && firstScoreId ?
            URLHandler.company.questionnaireScore(company.id, overview.id, currentCampaign?.id, firstScoreId.toString())
        :   undefined;

    return (
        <Stack flexDirection={"row"} gap={theme.spacing(2)} justifyContent={"space-between"} alignItems={"center"}>
            <Stack flexDirection="row" gap={theme.spacing(2)} alignItems={"center"}>
                <FoodsBadge
                    size={7}
                    icon={"questionnaireAnswer"}
                    boxProps={{
                        sx: {
                            color: datavizColors.variant[700],
                            backgroundColor: datavizColors.variant[100],
                            borderRadius: "8px",
                        },
                    }}
                />
                <Stack>
                    <Typography variant="h3">
                        {t("company.overview_one.many_scores.questionnaire_title", "My questionnaire")}
                    </Typography>
                </Stack>
                {/* What is the condition to validate it */}
                <ValidatedTag state={validatedState} />
            </Stack>
            <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                <GoToSsqButton url={url} isLocked={overview?.post?.isLocked} completion={overview?.post?.completion} />
            </Stack>
        </Stack>
    );
};
